import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TemplateBuilder from '../components/study/TemplateBuilder';
import { templateApi } from '../utils/studyApiService';
import './TemplateFormPage.css';

const emptyField = () => ({
  key: '',
  label: '',
  type: 'string',
  required: false,
});

const TemplateFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState([emptyField()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplate = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await templateApi.get(id);
      if (res.success) {
        const tpl = res.data;
        setName(tpl.name || '');
        setDescription(tpl.description || '');
        const existingFields = tpl.schema?.fields || [];
        setFields(existingFields.length > 0 ? existingFields : [emptyField()]);
      }
    } catch (e) {
      setError(e.message || '템플릿을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (isEdit) fetchTemplate();
  }, [isEdit, fetchTemplate]);

  const validate = () => {
    if (!name.trim()) return '템플릿 이름을 입력해주세요.';
    if (fields.length === 0) return '필드를 최소 1개 이상 추가해주세요.';

    for (const f of fields) {
      if (!f.key || !f.key.trim()) return '모든 필드의 키(key)를 입력해주세요.';
    }

    const keys = fields.map((f) => f.key);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) return '필드 키가 중복됩니다.';

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    const payload = {
      name: name.trim(),
      description: description.trim(),
      schema: { fields },
    };

    try {
      if (isEdit) {
        await templateApi.update(id, payload);
      } else {
        await templateApi.create(payload);
      }
      navigate('/templates');
    } catch (e) {
      setError(e.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="tpl-form-loading">불러오는 중...</div>;
  }

  return (
    <div className="tpl-form-page">
      <div className="tpl-form-nav">
        <button className="tpl-form-back-btn" onClick={() => navigate('/templates')}>
          ← 목록
        </button>
        <h1 className="tpl-form-page-title">
          {isEdit ? '템플릿 수정' : '새 템플릿'}
        </h1>
      </div>

      <form className="tpl-form" onSubmit={handleSubmit}>
        {error && <div className="tpl-form-error">{error}</div>}

        <div className="tpl-form-section">
          <label className="tpl-form-label">
            템플릿 이름 <span className="tpl-form-required">*</span>
          </label>
          <input
            className="tpl-form-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: 종목 분석 기본형"
            maxLength={100}
          />
        </div>

        <div className="tpl-form-section">
          <label className="tpl-form-label">설명</label>
          <textarea
            className="tpl-form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="이 템플릿의 용도나 특징을 간단히 설명해주세요."
            rows={3}
            maxLength={500}
          />
        </div>

        <div className="tpl-form-section">
          <label className="tpl-form-label">
            필드 구성 <span className="tpl-form-required">*</span>
          </label>
          <TemplateBuilder fields={fields} onChange={setFields} />
        </div>

        <div className="tpl-form-actions">
          <button
            type="button"
            className="tpl-form-btn tpl-form-btn-cancel"
            onClick={() => navigate('/templates')}
          >
            취소
          </button>
          <button
            type="submit"
            className="tpl-form-btn tpl-form-btn-save"
            disabled={saving}
          >
            {saving ? '저장 중...' : isEdit ? '수정 완료' : '템플릿 생성'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TemplateFormPage;
